import React, { useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/auth";
import { useDemoBrand } from "@/lib/demoBrand";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

// Starts a background render of every beat and polls until it finishes.
export const rerecordAllBeats = async (slug, onProgress) => {
  let { data } = await apiClient.post(`/admin/demos/${slug}/tour/render`);
  while (data.state === "running") {
    onProgress?.(data);
    await wait(2000);
    ({ data } = await apiClient.get(`/admin/demos/${slug}/tour/render`));
  }
  if (data.state === "error") throw new Error(data.error || "Render failed");
  return data;
};

export default function RerecordAllButton({ onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const { slug } = useDemoBrand();

  const run = async () => {
    setOpen(false);
    setBusy(true);
    try {
      const data = await rerecordAllBeats(slug, setProgress);
      toast.success(`Re-recorded ${data.done} beats in the current voice`);
      await onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Re-record failed");
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={busy}
        data-testid="demo-tour-rerecord-all"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-amber-500/10 text-slate-300 hover:text-amber-300 border border-white/10 hover:border-amber-500/40 font-mono text-[11px] font-semibold tracking-[0.14em] uppercase whitespace-nowrap transition-colors disabled:opacity-60"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        {busy ? `Recording${progress ? ` ${progress.done}/${progress.total}` : "…"}` : "Re-record all"}
      </button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="demo-tour-rerecord-dialog" className="bg-[#0C1322] border-white/10 text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle>Re-record every beat?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Every narration line will be re-recorded in the current ElevenLabs voice, keeping your edited wording. This uses ElevenLabs credits and takes about a minute.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="demo-tour-rerecord-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="demo-tour-rerecord-confirm" onClick={run}>
              Re-record all
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
